import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { MessageCircle, Send, Users, ChevronDown, ChevronUp, Phone, Search, ExternalLink } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { getFromLocalStorage } from '../mock';

const BACKEND_URL = ''; // same-origin: reverse proxy routes /api/* to backend
const getHeaders = () => ({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${getFromLocalStorage('accessToken')}` });

const WhatsAppModule = ({ customers, jobs }) => {
  const [templates, setTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [message, setMessage] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [mode, setMode] = useState('single'); // single | bulk
  const [search, setSearch] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    loadTemplates();
    loadHistory();
  }, []);

  const loadTemplates = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/whatsapp/templates`, { headers: getHeaders() });
      if (res.ok) { const d = await res.json(); setTemplates(d.templates || []); }
    } catch (e) { console.error('Load templates error:', e); }
  };

  const loadHistory = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/whatsapp/history?limit=30`, { headers: getHeaders() });
      if (res.ok) { const d = await res.json(); setHistory(d.messages || []); }
    } catch (e) { console.error('Load WA history error:', e); }
  };

  const applyTemplate = (tpl) => {
    setSelectedTemplate(tpl.id);
    let text = tpl.text;
    const cust = customers.find(c => c.id === selectedCustomer);
    if (cust) text = text.replace('{customer_name}', cust.name || '');
    const custJobs = jobs.filter(j => j.customerId === selectedCustomer);
    const remaining = custJobs.reduce((s, j) => s + (j.remainingBalance || 0), 0);
    text = text.replace('{amount}', remaining.toLocaleString());
    text = text.replace('{date}', new Date().toLocaleDateString('tr-TR'));
    if (cust?.carPlate) text = text.replace('{plate}', cust.carPlate);
    setMessage(text);
  };

  const handleSend = async () => {
    if (!selectedCustomer || !message) {
      toast({ title: "Hata", description: "Müşteri seçin ve mesaj yazın.", variant: "destructive" }); return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/whatsapp/send`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ customerId: selectedCustomer, templateId: selectedTemplate, message }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Hata');
      window.open(data.waLink, '_blank');
      toast({ title: "WhatsApp Açıldı", description: "Mesaj WhatsApp'ta gönderilmeye hazır." });
      loadHistory();
    } catch (e) { toast({ title: "Hata", description: e.message, variant: "destructive" }); }
  };

  const handleBulkSend = async () => {
    if (selectedCustomers.length === 0 || !message) {
      toast({ title: "Hata", description: "Müşteri seçin ve mesaj yazın.", variant: "destructive" }); return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/whatsapp/bulk-send`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ customerIds: selectedCustomers, templateId: selectedTemplate, message }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Hata');
      const okResults = data.results.filter(r => r.status === 'ok');
      okResults.forEach(r => window.open(r.waLink, '_blank'));
      toast({ title: "Toplu Mesaj", description: `${okResults.length}/${data.results.length} kişi için WhatsApp açıldı.` });
      loadHistory();
    } catch (e) { toast({ title: "Hata", description: e.message, variant: "destructive" }); }
  };

  const toggleBulkCustomer = (id) => {
    setSelectedCustomers(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
  };

  const filteredCustomers = search
    ? customers.filter(c => c.name?.toLowerCase().includes(search.toLowerCase()) || c.phone?.includes(search))
    : customers;

  const customersWithPhone = filteredCustomers.filter(c => c.phone);

  return (
    <div className="space-y-4">
      {/* Mode Toggle */}
      <div className="flex gap-2">
        <Button size="sm" onClick={() => setMode('single')} data-testid="wa-single-mode"
          className={mode === 'single' ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-300'}>
          <MessageCircle className="h-3 w-3 mr-1" />Tekli Mesaj
        </Button>
        <Button size="sm" onClick={() => setMode('bulk')} data-testid="wa-bulk-mode"
          className={mode === 'bulk' ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-300'}>
          <Users className="h-3 w-3 mr-1" />Toplu Mesaj
        </Button>
        <Button size="sm" variant="outline" onClick={() => setShowHistory(!showHistory)}
          className="ml-auto border-gray-600 text-gray-300">
          {showHistory ? <ChevronUp className="h-3 w-3 mr-1" /> : <ChevronDown className="h-3 w-3 mr-1" />}Geçmiş
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Left: Customer Selection */}
        <div>
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input placeholder="Müşteri veya telefon ara..." value={search} onChange={e => setSearch(e.target.value)}
              className="bg-gray-800 border-gray-700 text-white pl-10 h-8 text-sm" />
          </div>

          {mode === 'single' ? (
            <Select value={selectedCustomer} onValueChange={v => { setSelectedCustomer(v); setMessage(''); setSelectedTemplate(null); }}>
              <SelectTrigger className="bg-gray-800 border-gray-700 text-white" data-testid="wa-customer-select">
                <SelectValue placeholder="Müşteri seçin" />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-gray-700 max-h-60">
                {customersWithPhone.map(c => (
                  <SelectItem key={c.id} value={c.id} className="text-white hover:bg-gray-800">
                    <span>{c.name}</span>
                    <span className="text-gray-400 ml-2 text-xs">{c.phone}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="bg-gray-800/60 border border-gray-700/40 rounded-lg max-h-48 overflow-y-auto p-2 space-y-1">
              {customersWithPhone.map(c => (
                <label key={c.id} className="flex items-center gap-2 cursor-pointer text-xs py-1 px-2 rounded hover:bg-gray-700/50">
                  <input type="checkbox" checked={selectedCustomers.includes(c.id)}
                    onChange={() => toggleBulkCustomer(c.id)} className="rounded w-3 h-3" />
                  <span className="text-white">{c.name}</span>
                  <span className="text-gray-400">{c.phone}</span>
                </label>
              ))}
              {selectedCustomers.length > 0 && (
                <p className="text-green-400 text-xs mt-1">{selectedCustomers.length} müşteri seçili</p>
              )}
            </div>
          )}
        </div>

        {/* Right: Template + Message */}
        <div className="space-y-2">
          <p className="text-gray-400 text-xs">Hazır Şablonlar</p>
          <div className="grid grid-cols-2 gap-1 max-h-36 overflow-y-auto">
            {templates.map(t => (
              <Button key={t.id} size="sm" variant="outline"
                onClick={() => applyTemplate(t)} data-testid={`wa-tpl-${t.id}`}
                className={`border-gray-700 text-xs h-7 justify-start truncate ${selectedTemplate === t.id ? 'border-green-500 text-green-400' : 'text-gray-300'}`}>
                {t.name}
              </Button>
            ))}
          </div>

          <Textarea value={message} onChange={e => setMessage(e.target.value)} rows={4}
            placeholder="Mesajınızı yazın veya şablon seçin..."
            className="bg-gray-800 border-gray-700 text-white text-sm" data-testid="wa-message-input" />

          <Button onClick={mode === 'single' ? handleSend : handleBulkSend}
            className="w-full bg-green-600 hover:bg-green-700 text-white" data-testid="wa-send-btn">
            <Send className="h-4 w-4 mr-2" />{mode === 'single' ? 'WhatsApp ile Gönder' : `${selectedCustomers.length} Kişiye Gönder`}
          </Button>
        </div>
      </div>

      {/* History */}
      {showHistory && (
        <Card className="bg-gray-800/50 border-gray-700/40">
          <CardHeader className="pb-2"><CardTitle className="text-white text-sm">Mesaj Geçmişi</CardTitle></CardHeader>
          <CardContent className="max-h-48 overflow-y-auto space-y-1.5">
            {history.length === 0 ? <p className="text-gray-400 text-xs text-center py-4">Henüz mesaj yok</p> :
              history.map(m => (
                <div key={m.id} className="bg-gray-900/50 rounded p-2 text-xs border border-gray-700/30">
                  <div className="flex justify-between items-center">
                    <span className="text-white font-medium">{m.customerName}</span>
                    <span className="text-gray-500">{new Date(m.sentAt).toLocaleString('tr-TR')}</span>
                  </div>
                  <p className="text-gray-400 truncate mt-0.5">{m.message}</p>
                  <p className="text-gray-500 mt-0.5">Gönderen: {m.sentByName || m.sentBy}</p>
                </div>
              ))
            }
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default WhatsAppModule;
